import { HttpError } from '@/queries/auth-error'
import { isProdEnv } from '@/utils/isProdEnv'
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'

const integrations = [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]

function isHttpErrorEvent(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  if (hint.originalException instanceof HttpError) return true

  const exceptionValues = event.exception?.values ?? []
  return exceptionValues.some((exception) => exception.type === 'HttpError')
}

export default function sentryPlugin(app: App): void {
  if (isProdEnv()) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['*.lyka.com.au'],
      // Performance Monitoring
      tracesSampleRate: 0.1, //  Capture 10% of the transactions
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      beforeSend(event, hint) {
        // we don't want any API errors to be logged in Sentry, since that's Honeycomb's job.
        // Sentry is used only to log typical JS errors , not http errors
        if (isHttpErrorEvent(event, hint)) return null
        return event
      },
    })
  } else {
    console.log(`Sentry is not enabled at ${import.meta.env.PROD ? 'PROD' : 'DEV'} environment.`)
  }
}
